import { Button } from '@cottage/design-system/internal-tooling/ui'
import type { CaseDetail, CaseListItem } from '@/services/projections'
import { formatDate } from './projectionsFormat'
import { useDialogFocusTrap } from './useDialogFocusTrap'

type OverrideCaseDialogProps = {
  caseName: string
  fileName: string
  targetCase: CaseListItem | null
  selectedCase: CaseDetail | null
  onCancel: () => void
  onConfirm: () => void
}

function savedMetaLine(
  targetCase: CaseListItem | null,
  selectedCase: CaseDetail | null,
): string | null {
  if (!targetCase) return null
  const isSelectedTarget = selectedCase != null && selectedCase.id === targetCase.id
  const savedAt = isSelectedTarget
    ? (selectedCase.saved_at ?? selectedCase.updated_at)
    : (targetCase.saved_at ?? targetCase.updated_at)
  if (!savedAt) return null
  const updatedBy = isSelectedTarget ? selectedCase.updated_by : undefined
  const usesSavedAt = isSelectedTarget
    ? selectedCase.saved_at != null
    : targetCase.saved_at != null
  const dateLabel = usesSavedAt
    ? `Last saved ${formatDate(savedAt)}`
    : `Last updated ${formatDate(savedAt)}`
  return updatedBy ? `${dateLabel} by ${updatedBy}` : dateLabel
}

export default function OverrideCaseDialog({
  caseName,
  fileName,
  targetCase,
  selectedCase,
  onCancel,
  onConfirm,
}: OverrideCaseDialogProps) {
  const dialogRef = useDialogFocusTrap(true, onCancel)

  const savedMeta = savedMetaLine(targetCase, selectedCase)

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 p-4"
      onClick={onCancel}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="override-case-title"
        className="relative w-full max-w-lg rounded-xl bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="override-case-title" className="text-sm font-semibold text-gray-900">
          Override &apos;{caseName}&apos;?
        </h3>
        <p className="mt-3 text-sm text-gray-600">
          This replaces every driver, one-time item and the cash policy in this case with the
          contents of {fileName}. Cached inputs for inactive driver types are cleared. Notes in the
          file are added or updated; other notes are kept. Nothing is saved until the save completes
          — Discard restores the current case.
        </p>
        {savedMeta ? <p className="mt-2 text-xs text-gray-500">{savedMeta}</p> : null}
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" size="md" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="danger" size="md" onClick={onConfirm}>
            Override case
          </Button>
        </div>
      </div>
    </div>
  )
}
